<template>
  <BasicNavbar />
  <div
    class="w-full h-full flex justify-center mt-10 tablet-v:mt-[56px] desktop:mt-[126px] px-4 pb-3"
  >
    <div class="w-full max-w-[440px]">
      <!-- Header -->
      <div class="flex items-center justify-between mb-[56px]">
        <div
          class="font-primary font-medium text-[26px] leading-[22px] tracking-[-0.38px] text-gray-800"
        >
          Verify your identity
        </div>
        <RouterLink
          :to="onboardingRoute"
          class="font-primary text-[14px] leading-[18px] mt-1 text-green-900"
          :class="skipClasses"
        >
          Skip
        </RouterLink>
      </div>

      <!-- Form -->
      <div class="font-secondary text-[13px] leading-[18px] text-gray-800 mb-5">
        Confirm your identity with a government issued ID to provide you with
        stronger legal rights for your work
      </div>

      <Alert variant="info" :icon="InfoIcon" class="mb-5">
        Some jurisdictions require identity verification to officially register
        your copyright within that country.
      </Alert>

      <Button
        class="w-full!"
        variant="primary"
        size="lg"
        :loading="loading"
        @click="handleVerify"
      >
        Get started
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Alert, Button } from '@dacr/library'
import { InfoIcon } from '@dacr/icons'
import BasicNavbar from '@/shared/components/BasicNavbar.vue'
import { IdentityVerificationType } from '@/shared/graphql/router/graphql'
import { graphql } from '@/shared/graphql/router'
import { useMutation } from '@vue/apollo-composable'
import { getStripe } from '@/shared/services/stripe'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useFeatureFlag } from '@/shared/composables/useFeatureFlag'
import { FeatureFlag } from '@/shared/stores/featureFlags'
import { pricingCheckoutEntryRoute } from '@/features/onboarding/usePricingCheckoutResolver'

const { mutate: verifyIdentity } = useMutation(
  graphql(`
    mutation VerifyIdentity($input: VerifyIdentityInput!) {
      verifyIdentity(input: $input) {
        status
        clientSecret
        sessionId
        message
        verifiedAt
      }
    }
  `),
  {
    clientId: 'router',
  }
)

const router = useRouter()
const toastStore = useToastStore()
const idenFlagEnabled = useFeatureFlag(FeatureFlag.Iden)

const loading = ref(false)

const onboardingRoute = computed(() => pricingCheckoutEntryRoute())

const skipClasses = computed(() => ({
  ...(loading.value && {
    'opacity-50': true,
    'pointer-events-none': true,
  }),
}))

const handleVerify = async () => {
  if (!idenFlagEnabled.value) return

  loading.value = true

  try {
    const result = await verifyIdentity({
      input: {
        type: IdentityVerificationType.Document,
      },
    })

    const clientSecret = result?.data?.verifyIdentity.clientSecret
    if (!clientSecret) return

    const stripe = await getStripe()
    if (!stripe) return

    await stripe.verifyIdentity(clientSecret)

    router.push(onboardingRoute.value)
  } catch (error) {
    console.error(error)

    toastStore.showToast({
      type: ToastType.Error,
      content: 'Something went wrong - please try again later.',
      error: error as Error,
    })
  } finally {
    loading.value = false
  }
}
</script>
